/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone } from 'lucide-react';

const PaymentAccessModal = ({
  isOpen,
  onClose,
  contactPerson,
  companyName
}) => {
  const modalRef = useRef(null);

  // Format contact details
  const contactEmail = contactPerson?.email;
  const contactPhone = contactPerson?.phoneNumber
    ? (() => {
      let cleaned = (contactPerson.phoneNumber).replace(/\D/g, "");
      cleaned = cleaned.replace(/^0+/, "");
      if (cleaned.startsWith("91") && cleaned.length === 12) {
        const num = cleaned.substring(2);
        return `+91 ${num.slice(0, 5)} ${num.slice(5)}`;
      }
      if (cleaned.startsWith("1") && cleaned.length === 11) cleaned = cleaned.substring(1);
      if (cleaned.length === 10) {
        return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
      return `+${cleaned}`;
    })()
    : null;

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            ref={modalRef}
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden"
          >
            {/* Header with your exact gradient */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  Complete Your Booking
                </h3>
                <button
                  onClick={onClose}
                  className="text-slate-300 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Simple message */}
              <div className="text-center mb-6">
                <p className="text-slate-600">
                  Please contact sales to complete your booking.
                </p>
              </div>

              {/* Contact options - only shown if available */}
              {(contactEmail || contactPhone) && (
                <div className="space-y-3">
                  {contactEmail && (
                    <a
                      href={`mailto:${contactEmail}`}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-slate-700 border border-slate-200"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="text-sm font-medium">{contactEmail}</span>
                    </a>
                  )}

                  {contactPhone && (
                    <a
                      href={`tel:${contactPhone.replace(/\s/g, '')}`}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors text-slate-700 border border-slate-200"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm font-medium">{contactPhone}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentAccessModal;